import { useEffect, useState } from 'react';
import { Plus, Trash2, Edit, Save, X, Loader2, Star } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { SingleUploader } from '../../components/MediaUploader';
import api, { resolveMedia } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

const EMPTY = { name: '', role: '', quote: '', avatar: '', rating: 5, order: 0 };

export default function AdminTestimonials() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  const load = () => api.get('/admin/testimonials').then(r => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const open = (item) => { setEditing(item || 'new'); setForm(item || { ...EMPTY, order: items.length + 1 }); };
  const close = () => { setEditing(null); setForm(EMPTY); };
  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, rating: parseInt(form.rating) || 5, order: parseInt(form.order) || 0 };
      if (editing && editing !== 'new') await api.put('/admin/testimonials/' + editing.id, payload);
      else await api.post('/admin/testimonials', payload);
      toast({ title: 'Saved' });
      close(); load();
    } catch { toast({ title: 'Failed', variant: 'destructive' }); } finally { setSaving(false); }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete?')) return;
    await api.delete('/admin/testimonials/' + id);
    toast({ title: 'Deleted' });
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="text-2xl font-bold text-neutral-900">Testimonials</h1><p className="text-sm text-neutral-500">Client reviews shown on homepage</p></div>
        <Button onClick={() => open(null)} className="btn-primary gap-2"><Plus className="w-4 h-4" /> Add Testimonial</Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((t) => (
          <div key={t.id} className="bg-white p-5 rounded-xl border border-neutral-200">
            <div className="flex gap-1 text-[#DC2626] mb-2">{Array.from({ length: t.rating || 5 }).map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-current" />)}</div>
            <p className="text-sm text-neutral-700 italic line-clamp-3">{t.quote}</p>
            <div className="mt-3 flex items-center gap-3">
              <img src={resolveMedia(t.avatar) || '/assets/logo.png'} alt={t.name} className="w-10 h-10 rounded-full object-cover bg-neutral-100" />
              <div><p className="text-sm font-semibold text-neutral-900">{t.name}</p><p className="text-xs text-neutral-500">{t.role}</p></div>
            </div>
            <div className="flex gap-1 mt-3">
              <Button size="sm" variant="outline" onClick={() => open(t)}><Edit className="w-3.5 h-3.5" /></Button>
              <Button size="sm" variant="outline" onClick={() => remove(t.id)} className="text-red-600 border-red-200"><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={close}>
          <div className="bg-white rounded-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-neutral-900">{editing === 'new' ? 'New Testimonial' : 'Edit Testimonial'}</h2>
              <button onClick={close}><X className="w-5 h-5 text-neutral-500" /></button>
            </div>
            <div className="space-y-4">
              <SingleUploader value={form.avatar} onChange={(v) => setForm(f => ({...f, avatar: v}))} label="Avatar" />
              <div><label className="text-sm font-medium text-neutral-700 mb-1 block">Name *</label><Input required value={form.name} onChange={(e) => setForm(f => ({...f, name: e.target.value}))} /></div>
              <div><label className="text-sm font-medium text-neutral-700 mb-1 block">Role</label><Input value={form.role} onChange={(e) => setForm(f => ({...f, role: e.target.value}))} placeholder="Homebuyer, Cebu City" /></div>
              <div><label className="text-sm font-medium text-neutral-700 mb-1 block">Quote</label><Textarea rows={4} value={form.quote} onChange={(e) => setForm(f => ({...f, quote: e.target.value}))} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-sm font-medium text-neutral-700 mb-1 block">Rating</label><Input type="number" min="1" max="5" value={form.rating} onChange={(e) => setForm(f => ({...f, rating: e.target.value}))} /></div>
                <div><label className="text-sm font-medium text-neutral-700 mb-1 block">Order</label><Input type="number" value={form.order} onChange={(e) => setForm(f => ({...f, order: e.target.value}))} /></div>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={close}>Cancel</Button>
              <Button onClick={save} disabled={saving} className="btn-primary gap-2">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
